/**
 * CSV Generator Utility
 * Generates CSV files from validated user records
 */
import { CSV_HEADERS } from "../types";
/**
 * Escape a CSV value (wrap in quotes if contains comma, newline, or quotes)
 * @param value - Value to escape
 * @returns Escaped value safe for CSV
 */
function escapeCSVValue(value) {
    if (value === null || value === undefined) {
        return "";
    }
    const str = String(value);
    // If value contains comma, newline, or double quote, wrap in quotes
    if (str.includes(",") || str.includes("\n") || str.includes('"')) {
        // Escape double quotes by doubling them
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}
/**
 * Generate a CSV row from values
 * @param values - Array of values for the row
 * @returns CSV row string
 */
function generateCSVRow(values) {
    return values.map(escapeCSVValue).join(",");
}
/**
 * Generate CSV content from user records
 * @param records - Array of validated user records
 * @returns CSV content as string
 */
export function generateCSVContent(records) {
    const rows = [];
    // Add header row
    rows.push(generateCSVRow(CSV_HEADERS));
    // Add data rows
    for (const record of records) {
        // Use AccountName from reference user for Practice Name if available
        const practiceName = record.referenceUserAccountName || record.practiceName || "";
        const row = [
            record.lastName,
            record.firstName,
            record.middleName,
            record.suffix,
            record.npi,
            record.username,
            record.clinicalSystemId,
            record.gender,
            record.credentials,
            record.specialty,
            practiceName,
            record.officeNumber,
            record.siteNames,
            record.email,
            record.mobileNumber,
            record.mobileCarrier,
            record.pagerNumber,
            record.pagerCarrier,
            "Create", // Always "Create" for new users
            record.integrationMappings,
        ];
        rows.push(generateCSVRow(row));
    }
    return rows.join("\n");
}
/**
 * Generate a timestamped filename for the CSV
 * @returns Filename string
 */
export function generateCSVFilename() {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    return `UserProvisioning_${timestamp}.csv`;
}
/**
 * Create a downloadable CSV file and trigger download
 * @param records - Array of validated user records
 * @param filename - Optional filename (will generate if not provided)
 * @returns The generated filename
 */
export function downloadCSV(records, filename) {
    const csvContent = generateCSVContent(records);
    const finalFilename = filename || generateCSVFilename();
    // Create blob and download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = finalFilename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Clean up the URL object
    URL.revokeObjectURL(url);
    return finalFilename;
}
/**
 * Create a File object from user records (for upload)
 * @param records - Array of validated user records
 * @param filename - Optional filename
 * @returns File object ready for upload
 */
export function createCSVFile(records, filename) {
    const csvContent = generateCSVContent(records);
    const finalFilename = filename || generateCSVFilename();
    return new File([csvContent], finalFilename, { type: "text/csv" });
}
